import { useToast } from "@ligo/layout-design-core";
import { Errors } from "classes";
import { allOrdersKey, ss } from "consts";
import { FormFieldModel, OrderModel } from "models";
import {
  AttBaseProviderType,
  PermissionAttProviderType,
  useAttendDetail,
} from "providers";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import * as AttendanceService from "services/attendance";
import { aytyFormatError } from "utils";

type OrderType = {
  form?: FormFieldModel[];
  list?: OrderModel[];
};

type OrderContextType = {
  loading: boolean;
  orders?: OrderType;
  getOrders: (isReload?: boolean) => Promise<void>;
};

const OrderContext = createContext({} as OrderContextType);

const Provider = ({ children, all }: AttBaseProviderType) => {
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState<OrderType>();
  const { pIdOmni } = useAttendDetail();
  const { error, warning } = useToast();
  const { t } = useTranslation();

  const errorsResolver = useMemo(
    () => new Errors({ error, warning }, t),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    if (pIdOmni) {
      setLoading(true);
      setOrders(undefined);
    }
  }, [pIdOmni]);

  const getOrders = useCallback(
    async (isReload?: boolean) => {
      let current: OrderType = all[pIdOmni] ?? {};

      if (isReload || !current.list)
        await AttendanceService.getOrderList({ pIdOmni })
          .then(async ({ data }) => {
            if (data.idReturnAPI > 0) current.list = data.orderList;
            else warning({ description: t(aytyFormatError(data)) });
          })
          .catch(errorsResolver.defaultError);

      if (!current.form)
        await AttendanceService.getFormOrder({ pIdOmni })
          .then(async ({ data }) => {
            if (data.idReturnAPI > 0) current.form = data.bindListOrder;
            else warning({ description: t(aytyFormatError(data)) });
          })
          .catch(errorsResolver.defaultError);

      all[pIdOmni] = current;

      setOrders({ ...current });
      ss.setItem(allOrdersKey, JSON.stringify(all));

      setLoading(false);
    },
    [all, pIdOmni, errorsResolver, warning, t]
  );

  return (
    <OrderContext.Provider value={{ loading, orders, getOrders }}>
      {children}
    </OrderContext.Provider>
  );
};

export const OrderProvider = ({
  hasPermission = true,
  ...args
}: PermissionAttProviderType) => {
  if (!hasPermission) return <>{args.children}</>;

  return <Provider {...args} />;
};

export const useOrder = () => useContext(OrderContext);
